import { useState } from 'react'
import { FileText } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { PositionHistoryNote } from '@/types'

function HistoryNote({ note, ticker }: { note: PositionHistoryNote; ticker: string }) {
  const [imageFailed, setImageFailed] = useState(false)
  const imageUrl = `/api/coin/${encodeURIComponent(ticker)}/history/notes/${encodeURIComponent(note.id)}/image`
  const hasImage = Boolean(note.photo_file_id)

  return (
    <div className="text-xs text-muted-foreground pl-3 border-l-2 border-muted">
      <span className="font-medium">@{note.added_by}</span>{' '}
      <span className="opacity-60">{note.created_at?.slice(0, 10)}</span>
      {note.message && !(hasImage && note.message === '[image]') && (
        <p className="mt-0.5 whitespace-pre-wrap break-words">{note.message}</p>
      )}
      {hasImage && (
        imageFailed ? (
          <p className="mt-2">Image unavailable. <a className="underline" href={imageUrl} target="_blank" rel="noreferrer">Try opening image</a></p>
        ) : (
          <a className="block mt-2 w-fit" href={imageUrl} target="_blank" rel="noreferrer" aria-label={`Open history image for ${ticker}`}>
            <img
              className="max-h-72 max-w-full rounded-md border object-contain"
              src={imageUrl}
              alt={`History image for ${ticker} added by ${note.added_by}`}
              loading="lazy"
              onError={() => setImageFailed(true)}
            />
          </a>
        )
      )}
    </div>
  )
}

export function HistoryNotes({ notes, ticker }: { notes: PositionHistoryNote[]; ticker: string }) {
  const [showAll, setShowAll] = useState(false)
  if (!notes.length) return null

  return (
    <div className="space-y-2 pt-1">
      <h5 className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
        <FileText className="h-3.5 w-3.5" />
        Notes ({notes.length})
      </h5>
      {(showAll ? notes : notes.slice(0, 5)).map((note) => (
        <HistoryNote key={note.id} note={note} ticker={ticker} />
      ))}
      {notes.length > 5 && (
        <Button type="button" variant="ghost" size="sm" onClick={() => setShowAll(!showAll)} aria-expanded={showAll}>
          {showAll ? 'Show fewer notes' : `Show all ${notes.length} notes`}
        </Button>
      )}
    </div>
  )
}
